import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Brain, TrendingUp, ThumbsUp, RefreshCw, Target, Sparkles } from "lucide-react";
import ImprovementCard, { RuleProposal } from "@/components/learning/ImprovementCard";

interface LearningMetrics {
  totalDecisions: number;
  successRate: number;
  averageRating: number;
  regenerationRate: number;
  copyRate: number;
  shareRate: number;
}

interface OutcomeBreakdown {
  outcome: string;
  count: number;
}

const LearningAnalytics = () => {
  const [metrics, setMetrics] = useState<LearningMetrics>({
    totalDecisions: 0,
    successRate: 0,
    averageRating: 0,
    regenerationRate: 0,
    copyRate: 0,
    shareRate: 0,
  });
  const [outcomeData, setOutcomeData] = useState<OutcomeBreakdown[]>([]);
  const [ruleProposals, setRuleProposals] = useState<RuleProposal[]>([]);
  const [platformData, setPlatformData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLearningData();
  }, []);

  const fetchLearningData = async () => {
    try {
      // Fetch all decisions
      const { data: decisions, error } = await supabase
        .from("decision_learning")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (decisions && decisions.length > 0) {
        // Calculate metrics
        const total = decisions.length;
        const successCount = decisions.filter((d) => d.outcome === "success").length;
        const failCount = decisions.filter((d) => d.outcome === "fail").length;
        const ratingsWithScore = decisions.filter((d) => d.outcome_score !== null);
        const avgRating = ratingsWithScore.length > 0
          ? ratingsWithScore.reduce((sum, d) => sum + (d.outcome_score || 0), 0) / ratingsWithScore.length
          : 0;

        // Count by outcome reason for copy/share/regenerate
        const copyCount = decisions.filter((d) => d.outcome_reason?.includes("copied")).length;
        const shareCount = decisions.filter((d) => d.outcome_reason?.includes("shared")).length;
        const regenerateCount = decisions.filter((d) => d.outcome_reason?.includes("regenerated")).length;

        setMetrics({
          totalDecisions: total,
          successRate: (successCount / total) * 100,
          averageRating: avgRating,
          regenerationRate: (regenerateCount / total) * 100,
          copyRate: (copyCount / total) * 100,
          shareRate: (shareCount / total) * 100,
        });

        // Outcome breakdown
        const outcomeBreakdown: Record<string, number> = {};
        decisions.forEach((d) => {
          outcomeBreakdown[d.outcome] = (outcomeBreakdown[d.outcome] || 0) + 1;
        });
        setOutcomeData(
          Object.entries(outcomeBreakdown).map(([outcome, count]) => ({
            outcome: outcome.charAt(0).toUpperCase() + outcome.slice(1),
            count,
          }))
        );

        // Platform breakdown
        const platformBreakdown: Record<string, { success: number; total: number }> = {};
        decisions.forEach((d) => {
          if (!platformBreakdown[d.platform]) {
            platformBreakdown[d.platform] = { success: 0, total: 0 };
          }
          platformBreakdown[d.platform].total++;
          if (d.outcome === "success") {
            platformBreakdown[d.platform].success++;
          }
        });
        setPlatformData(
          Object.entries(platformBreakdown).map(([platform, data]) => ({
            platform: platform.charAt(0).toUpperCase() + platform.slice(1),
            successRate: (data.success / data.total) * 100,
            total: data.total,
          }))
        );

        // Generate trend data (last 7 days)
        const last7Days = Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          const dateStr = date.toISOString().split("T")[0];
          const dayDecisions = decisions.filter((d) => d.created_at.startsWith(dateStr));
          const daySuccess = dayDecisions.filter((d) => d.outcome === "success").length;
          return {
            date: date.toLocaleDateString("en-US", { weekday: "short" }),
            decisions: dayDecisions.length,
            successRate: dayDecisions.length > 0 ? (daySuccess / dayDecisions.length) * 100 : 0,
          };
        });
        setTrendData(last7Days);
      } else {
        // Demo data when no real data exists
        setMetrics({
          totalDecisions: 0,
          successRate: 0,
          averageRating: 0,
          regenerationRate: 0,
          copyRate: 0,
          shareRate: 0,
        });
        setOutcomeData([]);
        setTrendData([]);
      }

      // Fetch rule proposals from optimize-rules
      try {
        const { data: proposalData } = await supabase.functions.invoke("optimize-rules", {
          body: { action: "analyze" },
        });

        if (proposalData?.proposals) {
          setRuleProposals(proposalData.proposals);
        }
      } catch (e) {
        console.log("No rule proposals available yet");
      }
    } catch (error) {
      console.error("Error fetching learning data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProposalAction = (ruleId: string) => {
    setRuleProposals((prev) => prev.filter((p) => p.ruleId !== ruleId));
  };

  const COLORS = [
    "hsl(var(--brand-500))",
    "hsl(var(--brand-400))",
    "hsl(var(--brand-300))",
    "hsl(var(--muted-foreground))",
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Learning Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Brain className="h-4 w-4 text-brand-500" />
              Total Decisions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalDecisions}</div>
            <p className="text-xs text-muted-foreground">AI generations tracked</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Target className="h-4 w-4 text-success" />
              Success Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{metrics.successRate.toFixed(1)}%</div>
            <Progress value={metrics.successRate} className="h-1 mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ThumbsUp className="h-4 w-4 text-brand-500" />
              Avg Rating
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.averageRating.toFixed(1)}/5</div>
            <p className="text-xs text-muted-foreground">User satisfaction</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-destructive" />
              Regeneration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.regenerationRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Lower is better</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Copy Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.copyRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Content copied</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Share Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.shareRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Content shared</p>
          </CardContent>
        </Card>
      </div>

      {/* Rule Improvement Proposals */}
      {ruleProposals.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand-500" />
              AI Rule Improvements
              <Badge variant="secondary">{ruleProposals.length} pending</Badge>
            </h3>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {ruleProposals.map((proposal) => (
              <ImprovementCard
                key={proposal.ruleId}
                proposal={proposal}
                onApprove={handleProposalAction}
                onReject={handleProposalAction}
              />
            ))}
          </div>
        </div>
      )}

      {/* Charts */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="outcomes">Outcomes</TabsTrigger>
          <TabsTrigger value="platforms">Platforms</TabsTrigger>
        </TabsList>

        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>Learning Trends</CardTitle>
              <CardDescription>Decision volume and success rate over the last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              {trendData.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" className="text-xs" />
                      <YAxis yAxisId="left" className="text-xs" />
                      <YAxis yAxisId="right" orientation="right" className="text-xs" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="decisions"
                        stroke="hsl(var(--brand-500))"
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--brand-500))" }}
                        name="Decisions"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="successRate"
                        stroke="hsl(var(--success))"
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--success))" }}
                        name="Success Rate %"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  No trend data available yet. Start generating content to see trends.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="outcomes">
          <Card>
            <CardHeader>
              <CardTitle>Outcome Distribution</CardTitle>
              <CardDescription>Breakdown of decision outcomes</CardDescription>
            </CardHeader>
            <CardContent>
              {outcomeData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={outcomeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="outcome"
                      >
                        {outcomeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No outcome data available yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="platforms">
          <Card>
            <CardHeader>
              <CardTitle>Platform Performance</CardTitle>
              <CardDescription>Success rate by platform</CardDescription>
            </CardHeader>
            <CardContent>
              {platformData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={platformData} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" domain={[0, 100]} className="text-xs" />
                      <YAxis dataKey="platform" type="category" className="text-xs" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value: number) => [`${value.toFixed(1)}%`, "Success Rate"]}
                      />
                      <Bar dataKey="successRate" fill="hsl(var(--brand-500))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No platform data available yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LearningAnalytics;
